import { type BrandedSentryError, brandSentryError } from "@remi/error-core/brand";

import { BaseError } from "../BaseError";
import type { RemiApp } from "../remi-app-error/errorTypes/remi-app";
import type { RemiAppErrorInput } from "../remi-app-error/remiAppErrorInput";
import type { RemiPackage } from "../remi-error/errorTypes/remi-package";
import type { RemiErrorInput } from "../remi-error/remiErrorInput";
import type { RemiDomain } from "../remi-service/errorTypes/domain";
import type { ServiceErrorInput } from "../remi-service/serviceErrorInput";
import type { SentryErrorMetric } from "../types";
import type { ErrorSeverity } from "./severity";

type ServiceGeneric = RemiDomain | RemiPackage | RemiApp;
export type ErrorTypeGeneric =
  | ServiceErrorInput["errorType"]
  | RemiErrorInput["errorType"]
  | RemiAppErrorInput["errorType"];
type ErrorDataGeneric = Record<string, unknown> | undefined;

type BaseInput<T extends ServiceGeneric, U extends ErrorTypeGeneric, D extends ErrorDataGeneric> = {
  data?: D;
  severity: ErrorSeverity;
  service: T;
  errorType: U;
  cause?: Error;
};

export abstract class SentryError<
  T extends ServiceGeneric,
  U extends ErrorTypeGeneric,
  D extends ErrorDataGeneric = ErrorDataGeneric,
>
  extends BaseError
  implements BrandedSentryError
{
  private readonly _severity: ErrorSeverity;
  private readonly _data?: D;
  private _addedData?: Record<string, unknown>;
  private readonly _service: T;
  private readonly _errorType: U;
  private readonly _publicMessage: string;
  private readonly _metric?: SentryErrorMetric;
  readonly _customFingerprintPrefix?: string;
  constructor(
    input: BaseInput<T, U, D> & {
      name: string;
      message: string;
      publicMessage: string;
      customFingerprintPrefix?: string;
      cause?: Error;
      metric?: SentryErrorMetric;
    },
  ) {
    super({ message: input.message, cause: input.cause });
    this.name = input.name;
    this._data = input.data;
    this._severity = input.severity;
    this._service = input.service;
    this._errorType = input.errorType;
    this._publicMessage = input.publicMessage;
    this._customFingerprintPrefix = input.customFingerprintPrefix;
    this._metric = input.metric;
    brandSentryError(this);
  }

  get customFingerprintPrefix() {
    return this._customFingerprintPrefix;
  }

  abstract getSentryData(): {
    fingerprint: string[];
    tags: Record<string, string | boolean | number>;
    attributes?: Record<string, unknown>;
  };

  get severity(): ErrorSeverity {
    return this._severity;
  }

  get service(): T {
    return this._service;
  }

  get errorType(): U {
    return this._errorType;
  }

  get data(): D | undefined {
    return this._data;
  }

  get publicMessage(): string {
    return this._publicMessage;
  }

  get metric(): SentryErrorMetric | undefined {
    return this._metric;
  }

  addData(key: string, value: unknown): void {
    if (!this._addedData) {
      this._addedData = {};
    }
    this._addedData[key] = value;
  }

  protected get reportedData(): Record<string, unknown> | undefined {
    if (!this._addedData) {
      return this._data;
    }
    return { ...this._data, ...this._addedData };
  }
}
